import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const AdminSidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    dashboard: true,
    users: false,
    operations: false,
    financial: false,
    analytics: false,
    system: false,
    support: false
  });

  const navigationItems = [
    {
      id: 'dashboard',
      label: 'Dashboard Overview',
      icon: 'LayoutDashboard',
      path: '/admin-dashboard',
      items: [
        { label: 'Performance Metrics', panel: 'overview', icon: 'TrendingUp' },
        { label: 'System Health', panel: 'system-health', icon: 'Activity' },
        { label: 'Real-time Monitor', panel: 'monitor', icon: 'Monitor' }
      ]
    },
    {
      id: 'users',
      label: 'User Management',
      icon: 'Users',
      items: [
        { label: 'Customer Accounts', panel: 'customers', icon: 'User' },
        { label: 'Driver Management', panel: 'drivers', icon: 'Car' },
        { label: 'Administrator Roles', panel: 'admins', icon: 'Shield' },
        { label: 'Registration Requests', panel: 'registrations', icon: 'UserPlus' }
      ]
    },
    {
      id: 'operations',
      label: 'Operations Control',
      icon: 'Settings',
      items: [
        { label: 'Ride Coordination', panel: 'rides', icon: 'Navigation' },
        { label: 'Delivery Management', panel: 'deliveries', icon: 'Package' },
        { label: 'Dispatcher Tools', panel: 'dispatch', icon: 'Radio' },
        { label: 'Fleet Management', panel: 'fleet', icon: 'Truck' }
      ]
    },
    {
      id: 'financial',
      label: 'Financial Management',
      icon: 'DollarSign',
      items: [
        { label: 'Revenue Tracking', panel: 'revenue', icon: 'TrendingUp' },
        { label: 'M-Pesa Integration', panel: 'mpesa', icon: 'Smartphone' },
        { label: 'Commission Management', panel: 'commissions', icon: 'Percent' },
        { label: 'Settlement Processing', panel: 'settlements', icon: 'CreditCard' }
      ]
    },
    {
      id: 'rentals',
      label: 'Rentals & Leasing',
      icon: 'Car',
      items: [
        { label: 'Vehicle Review Queue', panel: 'vehicle-review', icon: 'ClipboardCheck', externalPath: '/vehicle-review-queue' },
        { label: 'Pricing Control Panel', panel: 'rental-pricing', icon: 'DollarSign', externalPath: '/rental-pricing-panel' },
        { label: 'Booking Oversight', panel: 'rental-bookings', icon: 'Calendar', externalPath: '/rental-booking-oversight' },
        { label: 'Corporate Contracts', panel: 'corporate-contracts', icon: 'Building', externalPath: '/corporate-contracts' },
        { label: 'Lease Requests Queue', panel: 'corporate-leases', icon: 'FileText' },
        { label: 'Corporate Accounts', panel: 'corporate-accounts', icon: 'Building2' },
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics & Reporting',
      icon: 'BarChart3',
      items: [
        { label: 'Business Intelligence', panel: 'analytics', icon: 'Brain' },
        { label: 'Performance Reports', panel: 'reports', icon: 'FileText' },
        { label: 'Market Analysis', panel: 'market', icon: 'Globe' },
        { label: 'Custom Reports', panel: 'custom-reports', icon: 'Filter' }
      ]
    },
    {
      id: 'system',
      label: 'System Administration',
      icon: 'Server',
      items: [
        { label: 'Platform Configuration', panel: 'config', icon: 'Settings' },
        { label: 'Security Settings', panel: 'security', icon: 'Lock' },
        { label: 'API Management', panel: 'api', icon: 'Code' },
        { label: 'System Logs', panel: 'logs', icon: 'FileText' },
        { label: 'Audit Log', panel: 'audit-log', icon: 'ClipboardList' },
      ]
    },
    {
      id: 'support',
      label: 'Support & Communications',
      icon: 'MessageSquare',
      items: [
        { label: 'Customer Service', panel: 'support', icon: 'Headphones' },
        { label: 'Notification Center', panel: 'notifications', icon: 'Bell' },
        { label: 'Communication Tools', panel: 'communications', icon: 'Mail' },
        { label: 'Feedback Management', panel: 'feedback', icon: 'MessageCircle' }
      ]
    },
    {
      id: 'promotions',
      label: 'Promotions Engine',
      icon: 'Percent',
      path: '/promotions-engine-screen',
      items: [
        { label: 'Promo Codes', panel: 'promo-codes', icon: 'Tag', externalPath: '/promotions-engine-screen' },
        { label: 'Loyalty Program', panel: 'loyalty', icon: 'Trophy', externalPath: '/promotions-engine-screen' },
        { label: 'Campaigns', panel: 'campaigns', icon: 'Megaphone', externalPath: '/promotions-engine-screen' },
        { label: 'Analytics', panel: 'analytics', icon: 'BarChart2', externalPath: '/promotions-engine-screen' }
      ]
    },
    {
      id: 'dispute',
      label: 'Dispute & Support',
      icon: 'Headphones',
      path: '/dispute-support-screen',
      items: [
        { label: 'All Tickets', panel: 'tickets', icon: 'Ticket', externalPath: '/dispute-support-screen' },
        { label: 'SOS Alerts', panel: 'sos', icon: 'AlertTriangle', externalPath: '/dispute-support-screen' },
        { label: 'Create Ticket', panel: 'new-ticket', icon: 'Plus', externalPath: '/dispute-support-screen' }
      ]
    },
    {
      id: 'ratings',
      label: 'Ratings & Reviews',
      icon: 'Star',
      path: '/ratings-review-screen',
      items: [
        { label: 'All Reviews', panel: 'all-reviews', icon: 'MessageSquare', externalPath: '/ratings-review-screen' },
        { label: 'Customer Reviews', panel: 'customer-reviews', icon: 'User', externalPath: '/ratings-review-screen' },
        { label: 'Driver Reviews', panel: 'driver-reviews', icon: 'Car', externalPath: '/ratings-review-screen' },
      ]
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp Channel',
      icon: 'MessageCircle',
      items: [
        { label: 'WA Bookings KPIs', panel: 'whatsapp', icon: 'BarChart2' },
        { label: 'Message Log', panel: 'whatsapp', icon: 'MessageSquare' },
        { label: 'Active Sessions', panel: 'whatsapp', icon: 'Activity' },
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev?.[sectionId]
    }));
  };

  const getCurrentPanel = () => {
    const params = new URLSearchParams(location?.search);
    return params?.get('panel') || 'overview';
  };

  const isActivePanel = (panel) => {
    return getCurrentPanel() === panel;
  };

  const isActiveSectionItem = (items) => {
    const currentPanel = getCurrentPanel();
    return items?.some(item => item?.panel === currentPanel);
  };

  const handlePanelNav = (panel, externalPath) => {
    if (externalPath) {
      navigate(externalPath);
    } else {
      navigate(`/admin-dashboard?panel=${panel}`);
    }
    onClose();
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-100 lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-16 h-[calc(100vh-4rem)] w-72 bg-surface border-r border-border z-100 transform transition-transform duration-300 ease-out-custom lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-text-primary">Administration</h2>
              <button
                onClick={onClose}
                className="lg:hidden p-1 rounded-lg hover:bg-surface-hover transition-colors duration-200"
                aria-label="Close sidebar"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-2">
            {navigationItems?.map((section) => (
              <div key={section?.id} className="space-y-1">
                <button
                  onClick={() => toggleSection(section?.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActiveSectionItem(section?.items)
                      ? 'bg-accent-50 text-primary border-r-2 border-accent' :'text-text-secondary hover:bg-surface-hover hover:text-primary'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon name={section?.icon} size={18} />
                    <span>{section?.label}</span>
                  </div>
                  <Icon
                    name="ChevronRight"
                    size={16}
                    className={`transform transition-transform duration-200 ${
                      expandedSections?.[section?.id] ? 'rotate-90' : ''
                    }`}
                  />
                </button>

                {/* Subsection Items */}
                {expandedSections?.[section?.id] && (
                  <div className="ml-4 space-y-1 animate-fade-in">
                    {section?.items?.map((item) => (
                      <button
                        key={item?.panel}
                        onClick={() => handlePanelNav(item?.panel, item?.externalPath)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
                          isActivePanel(item?.panel)
                            ? 'bg-accent-50 text-primary border-r-2 border-accent font-medium' :'text-text-secondary hover:bg-surface-hover hover:text-primary'
                        }`}
                      >
                        <Icon name={item?.icon} size={16} />
                        <span>{item?.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center space-x-3 p-3 bg-primary-50 rounded-lg">
              <div className="w-8 h-8 bg-success rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={16} color="white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-text-primary">System Status</p>
                <p className="text-xs text-success">All systems operational</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default AdminSidebar;